import {
  avg,
  buildExecutiveInsightCards,
  buildHealthActionQueue,
  buildHealthClosurePlan,
  buildHygieneActionQueue,
  buildHygieneClosurePlan,
  buildInclusionActionQueue,
  buildInclusionClosurePlan,
  buildInclusionHeroSummary,
  buildInsightsClosurePlan,
  buildInsightsHeroSummary,
  buildPriorityActionQueue,
  buildRootCauseSignals,
  buildWaterActionQueue,
  buildWaterClosurePlan,
  computeBlockCohesion,
  computeDataCoverage,
  computeFieldValidationNeed,
  computeInclusionScore,
  computeInsightConfidence,
  computeMetricConsistency,
  countPriorityGaps,
  deriveOwnerFromIssue,
  deriveTestingTarget,
  fmt,
  getFocusBlockNames,
  getReadinessLabel,
  pct,
  round1,
} from "@/utils/waterSafetyDerived";

const serializeExecutiveCards = (cards) =>
  cards.map((c) => ({
    key: c.key,
    title: c.title,
    description: c.description,
    severity: c.severity?.severity ?? c.severity,
    value: c.value,
  }));

const serializeRootSignals = (signals) =>
  signals.map((s) => ({
    key: s.key,
    title: s.title,
    description: s.description,
    score: s.score,
    score_label: s.scoreLabel,
  }));

const resolveInclusionCounts = (overview, inclusionMetrics) => {
  const rampDist = inclusionMetrics?.ramp ?? [];
  const educatorDist = inclusionMetrics?.special_educator ?? [];
  const rampYes = rampDist.find((d) => d.name === "Available")?.value ?? overview?.ramp_yes ?? 0;
  const rampNo =
    rampDist.find((d) => d.name === "Not Available")?.value ??
    Math.max(0, (overview?.total_schools ?? 0) - rampYes);
  const educatorNo =
    educatorDist.find((d) => d.name === "Not Available")?.value ?? overview?.special_educator_no ?? 0;
  return { rampNo, educatorNo };
};

export const buildWaterSafetyLocalGuidance = ({
  overview,
  blockData,
  bottomBlocks,
  highRiskSchools,
  inclusionMetrics,
}) => {
  if (!overview) return null;

  const { rampNo, educatorNo } = resolveInclusionCounts(overview, inclusionMetrics);
  const inclusionScore = computeInclusionScore(overview);
  const inclusionHero = buildInclusionHeroSummary(overview, inclusionScore);
  const totalSchools = overview.total_schools ?? 0;
  const thermalGap = Math.max(0, totalSchools - (overview.thermal_yes ?? 0));
  const lifeSavingGap = Math.max(0, totalSchools - (overview.life_saving_yes ?? 0));
  const shedGap = Math.max(0, totalSchools - (overview.kitchen_shed_yes ?? 0));
  const kitchenGap = Math.max(0, totalSchools - (overview.kitchen_dustbin_yes ?? 0));
  const priorityGaps = countPriorityGaps(overview);
  const healthActionQueue = buildHealthActionQueue(overview, highRiskSchools);
  const thermalTarget = deriveTestingTarget(overview.thermal_coverage, totalSchools, overview.thermal_yes);

  const executiveCards = buildExecutiveInsightCards(overview);
  const rootSignals = buildRootCauseSignals(overview);
  const insightsActionQueue = buildPriorityActionQueue(overview, blockData, bottomBlocks).map((row) => ({
    ...row,
    owner: deriveOwnerFromIssue(row.issue),
  }));

  return {
    insights: {
      hero_summary: buildInsightsHeroSummary(overview),
      executive_cards: serializeExecutiveCards(executiveCards),
      root_cause_signals: serializeRootSignals(rootSignals),
      action_queue: insightsActionQueue,
      closure_plan: buildInsightsClosurePlan(overview, blockData, bottomBlocks),
      expected_outcome: `Lift WSI from ${round1(overview.water_safety_index).toFixed(1)} toward ${round1(
        Math.min(100, (overview.water_safety_index ?? 0) + priorityGaps * 2)
      ).toFixed(1)} by closing ${fmt(overview.purification_no)} purification and ${fmt(overview.rwh_non_func)} RWH gaps.`,
      confidence_summary: `Data coverage ${Math.round(computeDataCoverage(overview))}%, metric consistency ${Math.round(
        computeMetricConsistency(overview)
      )}%, block cohesion ${Math.round(computeBlockCohesion(blockData))}%, field validation need ${Math.round(
        computeFieldValidationNeed(overview)
      )}%. Insight confidence ${computeInsightConfidence(overview)}%. Focus blocks: ${getFocusBlockNames(bottomBlocks, blockData, 3).join(", ") || "—"}.`,
    },
    water: {
      hero_summary: `WSI is ${round1(overview.water_safety_index).toFixed(1)} with tap water at ${pct(overview.tap_water_coverage)} and purification at ${pct(overview.purification_coverage)}. ${fmt(overview.purification_no)} schools lack purification and ${fmt(overview.rwh_non_func)} RWH units are non-functional.`,
      primary_action: `Convert ${fmt(overview.purification_no)} schools without purification into a block-wise remediation queue; current testing coverage is ${pct(overview.water_testing_coverage)}.`,
      action_queue: buildWaterActionQueue(overview, blockData, bottomBlocks).map((a) => ({
        title: a.title,
        description: a.description,
        priority_label: a.priority?.label ?? "Medium",
      })),
      closure_plan: buildWaterClosurePlan(overview, blockData, bottomBlocks),
    },
    hygiene: {
      hero_summary: `MDM hygiene score ${round1(avg(overview.classroom_hygiene_coverage, overview.toilet_hygiene_coverage, overview.kitchen_hygiene_coverage, overview.kitchen_shed_coverage, overview.kitchen_garden_coverage)).toFixed(1)} — classroom ${pct(overview.classroom_hygiene_coverage)}, kitchen ${pct(overview.kitchen_hygiene_coverage)}.`,
      primary_action: `Close ${fmt(shedGap)} kitchen shed gaps and ${fmt(kitchenGap)} kitchen dustbin gaps in lowest hygiene-score blocks first.`,
      action_queue: buildHygieneActionQueue(overview, blockData).map((a) => ({
        title: a.title,
        description: a.description,
        priority_label: a.priority?.label ?? "Medium",
      })),
      closure_plan: buildHygieneClosurePlan(overview, blockData),
    },
    health: {
      hero_summary: `Average health readiness ${pct(avg(overview.medical_checkup_coverage, overview.health_record_coverage, overview.first_aid_coverage, overview.life_saving_coverage, overview.thermal_coverage))} across ${fmt(totalSchools)} schools. Thermal gap: ${fmt(thermalGap)}; life-saving gap: ${fmt(lifeSavingGap)}.`,
      primary_action: `Prioritize ${(highRiskSchools ?? []).filter((s) => (s.risk_count ?? 0) >= 5).length} risk-score-5 schools; close ${fmt(thermalGap)} thermal and ${fmt(lifeSavingGap)} life-saving equipment gaps within ${healthActionQueue[0]?.deadline ?? 30} days.`,
      action_queue: healthActionQueue.map((a) => ({
        title: a.title,
        description: a.description,
        priority_label: a.priority?.label ?? "Medium",
        deadline_days: a.deadline ?? 30,
      })),
      closure_plan: buildHealthClosurePlan(overview, highRiskSchools),
      review_recommendation: `Run weekly review until thermal screening reaches ${pct(thermalTarget)} (from ${pct(overview.thermal_coverage)}) and all risk-score-5 schools are verified.`,
    },
    inclusion: {
      hero_summary: inclusionHero.summary,
      primary_action: inclusionHero.action,
      readiness_label: inclusionHero.readinessLabel,
      readiness_tone: inclusionHero.readinessTone,
      action_queue: buildInclusionActionQueue(overview, blockData, rampNo, educatorNo).map((a) => ({
        title: a.title,
        description: a.description,
        deadline_days: a.deadline ?? 30,
      })),
      closure_plan: buildInclusionClosurePlan(overview, rampNo, educatorNo),
    },
  };
};

export const buildWaterSafetyMetricsPayload = ({
  overview,
  blockData,
  bottomBlocks,
  highRiskSchools,
  scopeLabel,
}) => ({
  scope_label: scopeLabel,
  overview,
  bottom_blocks: (bottomBlocks ?? []).slice(0, 8),
  block_summary: (blockData ?? []).slice(0, 12).map((b) => ({
    block_name: b.block_name,
    total_schools: b.total_schools,
    water_safety_index: b.water_safety_index,
    hygiene_score: b.hygiene_score,
    health_readiness_score: b.health_readiness_score,
    inclusion_gap_pct: b.inclusion_gap_pct,
    risk_score: b.risk_score,
  })),
  high_risk_school_count: (highRiskSchools ?? []).length,
  critical_risk_school_count: (highRiskSchools ?? []).filter((s) => (s.risk_count ?? 0) >= 5).length,
});
